import {
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';
import { RowOpenMode } from '../table.models';

// Selector for the interactive children a row-open click/Enter must ignore
// (design handoff, "Step 5, streamlined", section 4): checkbox, button, a,
// input, and anything inside `.p-checkbox` (PrimeNG's checkbox host class)
// or `sh-menu-button` / `sh-action-menu`.
const INTERACTIVE_SELECTOR =
  'input, button, a, .p-checkbox, sh-menu-button, sh-action-menu';

/**
 * Makes a table row open a record on click/Enter, per table (design
 * handoff, "Step 5, streamlined: the data table pattern", section 4):
 * `navigate`, `peek` or `none`. In `none` mode it does nothing -- no
 * tabindex, no class, no emit.
 *
 * Usage:
 * `<tr [shRowOpen]="row" [shRowOpenMode]="'navigate'" [shRowOpenActive]="isOpen(row)" (rowOpen)="...">`.
 */
@Directive({
  selector: '[shRowOpen]',
  standalone: true,
})
export class RowOpenDirective<T = unknown> {
  readonly row = input<T | undefined>(undefined, { alias: 'shRowOpen' });
  readonly mode = input<RowOpenMode>('none', { alias: 'shRowOpenMode' });
  readonly active = input(false, { alias: 'shRowOpenActive' });

  readonly rowOpen = output<T>();

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);

  // Focusable and styled as clickable only when a mode is set.
  @HostBinding('class.row-openable')
  protected get isOpenable(): boolean {
    return this.mode() !== 'none';
  }

  @HostBinding('attr.tabindex')
  protected get tabIndex(): number | null {
    return this.mode() !== 'none' ? 0 : null;
  }

  // Peek mode only: the currently open row gets the selected-row style
  // (design handoff: row-selected background + amber inset bar, reused from
  // the table's own selected-row rule).
  @HostBinding('class.row-open')
  protected get isOpen(): boolean {
    return this.mode() === 'peek' && this.active();
  }

  @HostListener('click', ['$event'])
  protected onClick(event: MouseEvent): void {
    this._tryEmit(event.target as HTMLElement | null);
  }

  @HostListener('keydown.enter', ['$event'])
  protected onEnter(event: KeyboardEvent): void {
    if (this._tryEmit(event.target as HTMLElement | null)) {
      event.preventDefault();
    }
  }

  private _tryEmit(target: HTMLElement | null): boolean {
    if (this.mode() === 'none') return false;
    if (this._isInteractiveChild(target)) return false;

    const row = this.row();
    if (row === undefined) return false;

    this.rowOpen.emit(row);
    return true;
  }

  private _isInteractiveChild(target: HTMLElement | null): boolean {
    if (!target) return false;
    const host = this._host.nativeElement;
    const interactive = target.closest(INTERACTIVE_SELECTOR);
    return !!interactive && interactive !== host && host.contains(interactive);
  }
}
